import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const getAuthHeader = () => {
    const token = localStorage.getItem('mactech_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
    // Generic methods
    get: async (url, config = {}) => {
        return axios.get(url.startsWith('/api') ? url.replace('/api', API_URL) : `${API_URL}${url}`, { headers: getAuthHeader(), ...config });
    },
    put: async (url, data, config = {}) => {
        return axios.put(url.startsWith('/api') ? url.replace('/api', API_URL) : `${API_URL}${url}`, data, { headers: getAuthHeader(), ...config });
    },
    delete: async (url, config = {}) => {
        return axios.delete(url.startsWith('/api') ? url.replace('/api', API_URL) : `${API_URL}${url}`, { headers: getAuthHeader(), ...config });
    },
    post: async (url, data, config = {}) => {
        return axios.post(url.startsWith('/api') ? url.replace('/api', API_URL) : `${API_URL}${url}`, data, { headers: getAuthHeader(), ...config });
    },

    // Auth
    register: async (data) => {
        const response = await axios.post(`${API_URL}/auth/register`, data);
        return response.data;
    },

    login: async (data) => {
        const response = await axios.post(`${API_URL}/auth/login`, data);
        return response.data;
    },

    verifyEmail: async (data) => {
        const response = await axios.post(`${API_URL}/auth/verify-email`, data);
        return response.data;
    },

    resendVerification: async (email) => {
        const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
        return response.data;
    },

    forgotPassword: async (email) => {
        const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
        return response.data;
    },

    resetPassword: async (email, code, new_password) => {
        const response = await axios.post(`${API_URL}/auth/reset-password`, { email, code, new_password });
        return response.data;
    },

    sendPhoneCode: async (phone) => {
        const response = await axios.post(`${API_URL}/auth/send-phone-code`, { phone });
        return response.data;
    },

    verifyPhone: async (phone, code) => {
        const response = await axios.post(`${API_URL}/auth/verify-phone`, { phone, code });
        return response.data;
    },

    getMe: async () => {
        const response = await axios.get(`${API_URL}/auth/me`, { headers: getAuthHeader() });
        return response.data;
    },

    getSystemStatus: async () => {
        const response = await axios.get(`${API_URL}/system/status`);
        return response.data;
    },

    setupCreateAdmin: async (data) => {
        const response = await axios.post(`${API_URL}/auth/register`, data);
        return response.data;
    },

    // Applications
    getApplications: async () => {
        const response = await axios.get(`${API_URL}/applications`);
        return response.data;
    },

    getApplicationsWithPricing: async () => {
        const response = await axios.get(`${API_URL}/applications/pricing`);
        return response.data;
    },

    createApplication: async (data) => {
        const response = await axios.post(`${API_URL}/applications`, data, { headers: getAuthHeader() });
        return response.data;
    },

    updateApplication: async (id, data) => {
        const response = await axios.put(`${API_URL}/applications/${id}`, data, { headers: getAuthHeader() });
        return response.data;
    },

    deleteApplication: async (id) => {
        const response = await axios.delete(`${API_URL}/applications/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    updatePricing: async (id, data) => {
        const response = await axios.put(`${API_URL}/applications/${id}/pricing`, data, { headers: getAuthHeader() });
        return response.data;
    },

    // Site Content (CMS)
    getSiteContent: async () => {
        const response = await axios.get(`${API_URL}/site-content`);
        return response.data;
    },

    getFeatureImages: async () => {
        const response = await axios.get(`${API_URL}/feature-images`);
        return response.data;
    },

    submitEnterpriseContact: async (data) => {
        const response = await axios.post(`${API_URL}/enterprise-contact`, data);
        return response.data;
    },

    updateFeatureImage: async (key, url) => {
        const response = await axios.post(`${API_URL}/admin/feature-images`, { key, url }, { headers: getAuthHeader() });
        return response.data;
    },

    getEnterpriseContacts: async () => {
        const response = await axios.get(`${API_URL}/admin/enterprise-contacts`, { headers: getAuthHeader() });
        return response.data;
    },

    updateSiteContent: async (data) => {
        const response = await axios.put(`${API_URL}/site-content`, data, { headers: getAuthHeader() });
        return response.data;
    },

    // File Upload
    uploadFile: async (file, category = 'images') => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API_URL}/upload?category=${category}`, formData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    getFileUrl: (storagePath) => `${API_URL}/files/${storagePath}`,

    listMedia: async (category) => {
        const params = category ? `?category=${category}` : '';
        const response = await axios.get(`${API_URL}/media${params}`, { headers: getAuthHeader() });
        return response.data;
    },

    deleteMedia: async (fileId) => {
        const response = await axios.delete(`${API_URL}/media/${fileId}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Subscriptions
    startTrial: async (appId) => {
        const response = await axios.post(`${API_URL}/subscriptions/start-trial`, { app_id: appId }, { headers: getAuthHeader() });
        return response.data;
    },

    getMyApps: async () => {
        const response = await axios.get(`${API_URL}/subscriptions/my-apps`, { headers: getAuthHeader() });
        return response.data;
    },

    getMyDiscounts: async () => {
        const response = await axios.get(`${API_URL}/subscriptions/my-discounts`, { headers: getAuthHeader() });
        return response.data;
    },

    createSubscription: async (data) => {
        const response = await axios.post(`${API_URL}/subscriptions`, data, { headers: getAuthHeader() });
        return response.data;
    },

    getSubscriptions: async () => {
        const response = await axios.get(`${API_URL}/subscriptions`, { headers: getAuthHeader() });
        return response.data;
    },

    checkSubscription: async (appId) => {
        const response = await axios.get(`${API_URL}/subscriptions/check/${appId}`, { headers: getAuthHeader() });
        return response.data;
    },

    cancelSubscription: async (id) => {
        const response = await axios.delete(`${API_URL}/subscriptions/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Admin
    getAdminUsers: async () => {
        const response = await axios.get(`${API_URL}/admin/users`, { headers: getAuthHeader() });
        return response.data;
    },

    searchUsers: async (q) => {
        const response = await axios.get(`${API_URL}/admin/users/search?q=${encodeURIComponent(q)}`, { headers: getAuthHeader() });
        return response.data;
    },

    updateUser: async (id, data) => {
        const response = await axios.put(`${API_URL}/admin/users/${id}`, data, { headers: getAuthHeader() });
        return response.data;
    },

    deleteUser: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/users/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    assignParent: async (userId, parentUserId) => {
        const response = await axios.put(
            `${API_URL}/admin/users/${userId}/assign-parent`,
            { parent_user_id: parentUserId || null },
            { headers: getAuthHeader() }
        );
        return response.data;
    },

    getAdminSubscriptions: async () => {
        const response = await axios.get(`${API_URL}/admin/subscriptions`, { headers: getAuthHeader() });
        return response.data;
    },

    adminAssignSubscription: async (data) => {
        const response = await axios.post(`${API_URL}/admin/subscriptions/assign`, data, { headers: getAuthHeader() });
        return response.data;
    },

    extendSubscription: async (id, days) => {
        const response = await axios.post(`${API_URL}/admin/subscriptions/${id}/extend`, { days }, { headers: getAuthHeader() });
        return response.data;
    },

    giveDiscount: async (userId, data) => {
        const response = await axios.post(`${API_URL}/admin/users/${userId}/discount`, data, { headers: getAuthHeader() });
        return response.data;
    },

    getUserDiscounts: async (userId) => {
        const response = await axios.get(`${API_URL}/admin/users/${userId}/discounts`, { headers: getAuthHeader() });
        return response.data;
    },

    // Analytics
    getAnalytics: async () => {
        const response = await axios.get(`${API_URL}/admin/analytics`, { headers: getAuthHeader() });
        return response.data;
    },

    // Email Templates (CMS)
    getEmailTemplates: async () => {
        const response = await axios.get(`${API_URL}/admin/email-templates`, { headers: getAuthHeader() });
        return response.data;
    },
    getGaleriUsersHierarchy: async (force = false) => {
        const response = await axios.get(`${API_URL}/admin/galeri/users-hierarchy${force ? '?force=true' : ''}`, { headers: getAuthHeader() });
        return response.data;
    },
    updateEmailTemplate: async (key, data) => {
        const response = await axios.put(`${API_URL}/admin/email-templates/${key}`, data, { headers: getAuthHeader() });
        return response.data;
    },
    resetEmailTemplate: async (key) => {
        const response = await axios.post(`${API_URL}/admin/email-templates/${key}/reset`, {}, { headers: getAuthHeader() });
        return response.data;
    },

    // Subscription Packages (DB-driven pricing)
    getPackages: async (appId = null) => {
        const url = appId ? `${API_URL}/packages?app_id=${appId}` : `${API_URL}/packages`;
        const response = await axios.get(url);
        return response.data;
    },
    getAdminPackages: async () => {
        const response = await axios.get(`${API_URL}/admin/packages`, { headers: getAuthHeader() });
        return response.data;
    },
    createPackage: async (data) => {
        const response = await axios.post(`${API_URL}/admin/packages`, data, { headers: getAuthHeader() });
        return response.data;
    },
    updatePackage: async (id, data) => {
        const response = await axios.put(`${API_URL}/admin/packages/${id}`, data, { headers: getAuthHeader() });
        return response.data;
    },
    deletePackage: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/packages/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Export
    exportUsersCSV: async () => {
        const response = await axios.get(`${API_URL}/admin/export/users`, {
            headers: getAuthHeader(),
            responseType: 'blob'
        });
        return response.data;
    },

    exportSubscriptionsCSV: async () => {
        const response = await axios.get(`${API_URL}/admin/export/subscriptions`, {
            headers: getAuthHeader(),
            responseType: 'blob'
        });
        return response.data;
    },

    // Messages
    getContactMessages: async () => {
        const response = await axios.get(`${API_URL}/admin/messages`, { headers: getAuthHeader() });
        return response.data;
    },

    getUnreadMessageCount: async () => {
        const response = await axios.get(`${API_URL}/admin/messages/unread-count`, { headers: getAuthHeader() });
        return response.data;
    },

    markMessageRead: async (id) => {
        const response = await axios.put(`${API_URL}/admin/messages/${id}/read`, {}, { headers: getAuthHeader() });
        return response.data;
    },

    replyToMessage: async (id, replyText) => {
        const response = await axios.post(`${API_URL}/admin/messages/reply/${id}`, { reply_text: replyText }, { headers: getAuthHeader() });
        return response.data;
    },

    deleteContactMessage: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/messages/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Announcements
    getAnnouncements: async () => {
        const response = await axios.get(`${API_URL}/announcements`);
        return response.data;
    },

    createAnnouncement: async (data) => {
        const response = await axios.post(`${API_URL}/announcements`, data, { headers: getAuthHeader() });
        return response.data;
    },

    deleteAnnouncement: async (id) => {
        const response = await axios.delete(`${API_URL}/announcements/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Settings
    getSettings: async () => {
        const response = await axios.get(`${API_URL}/settings`);
        return response.data;
    },

    updateSettings: async (data) => {
        const response = await axios.put(`${API_URL}/settings`, data, { headers: getAuthHeader() });
        return response.data;
    },

    // Contact
    sendContactMessage: async (data) => {
        const response = await axios.post(`${API_URL}/contact`, data);
        return response.data;
    },

    // Seed
    seedData: async () => {
        const response = await axios.post(`${API_URL}/seed`);
        return response.data;
    },

    // Site Stats
    getSiteStats: async () => {
        const response = await axios.get(`${API_URL}/site-stats`);
        return response.data;
    },

    updateSiteStats: async (stats) => {
        const response = await axios.put(`${API_URL}/admin/site-stats`, { stats }, { headers: getAuthHeader() });
        return response.data;
    },

    // Carousel
    getCarousel: async () => {
        const response = await axios.get(`${API_URL}/carousel`);
        return response.data;
    },

    addCarouselImage: async (image_data) => {
        const response = await axios.post(`${API_URL}/admin/carousel`, { image_data }, { headers: getAuthHeader() });
        return response.data;
    },

    deleteCarouselImage: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/carousel/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // Application detail & images
    getApplicationDetail: async (appId) => {
        const response = await axios.get(`${API_URL}/applications/${appId}`);
        return response.data;
    },

    addAppImage: async (appId, image_data) => {
        const response = await axios.post(`${API_URL}/admin/applications/${appId}/images`, { image_data }, { headers: getAuthHeader() });
        return response.data;
    },

    deleteAppImage: async (appId, imageId) => {
        const response = await axios.delete(`${API_URL}/admin/applications/${appId}/images/${imageId}`, { headers: getAuthHeader() });
        return response.data;
    },

    // ==================== SECTORS ====================

    getSectors: async () => {
        const response = await axios.get(`${API_URL}/sectors`);
        return response.data;
    },

    getSector: async (slug) => {
        const response = await axios.get(`${API_URL}/sectors/${slug}`);
        return response.data;
    },

    checkSectorAccess: async (slug) => {
        const response = await axios.get(`${API_URL}/sectors/${slug}/check-access`, { headers: getAuthHeader() });
        return response.data;
    },

    getMySectorStatus: async (slug) => {
        const response = await axios.get(`${API_URL}/sectors/${slug}/my-status`, { headers: getAuthHeader() });
        return response.data;
    },

    // Admin Sectors
    getAdminSectors: async () => {
        const response = await axios.get(`${API_URL}/admin/sectors`, { headers: getAuthHeader() });
        return response.data;
    },

    createSector: async (data) => {
        const response = await axios.post(`${API_URL}/admin/sectors`, data, { headers: getAuthHeader() });
        return response.data;
    },

    updateSector: async (sectorId, data) => {
        const response = await axios.put(`${API_URL}/admin/sectors/${sectorId}`, data, { headers: getAuthHeader() });
        return response.data;
    },

    deleteSector: async (sectorId) => {
        const response = await axios.delete(`${API_URL}/admin/sectors/${sectorId}`, { headers: getAuthHeader() });
        return response.data;
    },

    getSectorStats: async (sectorId) => {
        const response = await axios.get(`${API_URL}/admin/sectors/${sectorId}/stats`, { headers: getAuthHeader() });
        return response.data;
    },

    // ==================== GALERI SECTOR DATA ====================

    getGaleriVehicles: async () => {
        const response = await axios.get(`${API_URL}/galeri/vehicles`, { headers: getAuthHeader() });
        return response.data;
    },

    createGaleriVehicle: async (data) => {
        const response = await axios.post(`${API_URL}/galeri/vehicles`, data, { headers: getAuthHeader() });
        return response.data;
    },

    updateGaleriVehicle: async (vehicleId, data) => {
        const response = await axios.put(`${API_URL}/galeri/vehicles/${vehicleId}`, data, { headers: getAuthHeader() });
        return response.data;
    },

    deleteGaleriVehicle: async (vehicleId) => {
        const response = await axios.delete(`${API_URL}/galeri/vehicles/${vehicleId}`, { headers: getAuthHeader() });
        return response.data;
    },

    getGaleriDashboard: async () => {
        const response = await axios.get(`${API_URL}/galeri/dashboard`, { headers: getAuthHeader() });
        return response.data;
    },

    // SSO
    generateSSOToken: async (redirectUrl, sectorSlug = '') => {
        const response = await axios.post(`${API_URL}/platform/sso/generate`, { redirect_url: redirectUrl, sector_slug: sectorSlug }, { headers: getAuthHeader() });
        return response.data;
    },

    // Reviews
    getReviews: async () => {
        const response = await axios.get(`${API_URL}/reviews?status=approved`);
        return response.data;
    },
    getReviewStats: async () => {
        const response = await axios.get(`${API_URL}/reviews/stats`);
        return response.data;
    },
    submitReview: async (data) => {
        const response = await axios.post(`${API_URL}/reviews`, data);
        return response.data;
    },
    getAdminReviews: async () => {
        const response = await axios.get(`${API_URL}/admin/reviews`, { headers: getAuthHeader() });
        return response.data;
    },
    approveReview: async (id) => {
        const response = await axios.put(`${API_URL}/admin/reviews/${id}/approve`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    rejectReview: async (id) => {
        const response = await axios.put(`${API_URL}/admin/reviews/${id}/reject`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    deleteReview: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/reviews/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    getAggregateRating: async () => {
        const response = await axios.get(`${API_URL}/seo/aggregate-rating`);
        return response.data;
    },

    // ==================== LEADS ====================
    submitLead: async (data) => {
        const response = await axios.post(`${API_URL}/leads`, data);
        return response.data;
    },
    getAdminLeads: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const response = await axios.get(`${API_URL}/admin/leads${qs ? `?${qs}` : ''}`, { headers: getAuthHeader() });
        return response.data;
    },
    getLeadStats: async () => {
        const response = await axios.get(`${API_URL}/admin/leads/stats`, { headers: getAuthHeader() });
        return response.data;
    },
    updateLead: async (id, data) => {
        const response = await axios.put(`${API_URL}/admin/leads/${id}`, data, { headers: getAuthHeader() });
        return response.data;
    },
    deleteLead: async (id) => {
        const response = await axios.delete(`${API_URL}/admin/leads/${id}`, { headers: getAuthHeader() });
        return response.data;
    },

    // ==================== LANDING CMS ====================
    getLandingSections: async (appSlug) => {
        const response = await axios.get(`${API_URL}/landing/${appSlug}`);
        return response.data;
    },
    getAdminLandingSections: async (appSlug) => {
        const response = await axios.get(`${API_URL}/admin/landing/${appSlug}`, { headers: getAuthHeader() });
        return response.data;
    },
    upsertLandingSection: async (appSlug, sectionKey, payload) => {
        const response = await axios.put(`${API_URL}/admin/landing/${appSlug}/${sectionKey}`, payload, { headers: getAuthHeader() });
        return response.data;
    },
    deleteLandingSection: async (appSlug, sectionKey) => {
        const response = await axios.delete(`${API_URL}/admin/landing/${appSlug}/${sectionKey}`, { headers: getAuthHeader() });
        return response.data;
    },
};
